import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, LogOut, RefreshCw, Inbox } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const http = axios.create({ baseURL: API, withCredentials: true });

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

export default function AdminLeadsPage() {
  const [authed, setAuthed] = useState(false);
  const [checked, setChecked] = useState(false);
  const [passcode, setPasscode] = useState("");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadLeads = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/admin/leads");
      setLeads(data);
      setAuthed(true);
    } catch {
      setAuthed(false);
    } finally {
      setLoading(false);
      setChecked(true);
    }
  }, []);

  useEffect(() => { loadLeads(); }, [loadLeads]);

  const login = async (e) => {
    e.preventDefault();
    try {
      await http.post("/admin/session", { passcode });
      setPasscode("");
      await loadLeads();
    } catch {
      toast.error("Invalid passcode.");
    }
  };

  const logout = async () => {
    await http.delete("/admin/session").catch(() => {});
    setAuthed(false);
    setLeads([]);
  };

  if (!checked) return <div className="min-h-screen bg-[#0b0b0c]" />;

  if (!authed) {
    return (
      <main data-testid="admin-login-page" className="min-h-screen bg-[#0b0b0c] flex items-center justify-center px-5">
        <div className="w-full max-w-sm bg-[#141416] border border-white/10 p-9">
          <div className="h-12 w-12 rounded-full bg-[#c5a059]/10 flex items-center justify-center text-[#c5a059] mb-6">
            <Lock size={20} />
          </div>
          <h1 className="font-display text-2xl font-medium text-[#faf8f5]">Lead Inbox</h1>
          <p className="mt-2 text-sm font-light text-white/50">Private — enter the team passcode to view booking enquiries.</p>
          <form onSubmit={login} className="mt-7 space-y-4">
            <input
              data-testid="admin-passcode-input"
              type="password"
              autoComplete="current-password"
              placeholder="Team passcode"
              value={passcode}
              onChange={(e) => setPasscode(e.target.value)}
              className="w-full bg-[#0b0b0c] border border-white/15 rounded-sm px-4 py-3 text-sm font-light text-white focus:outline-none focus:border-[#c5a059] transition-colors"
            />
            <button
              type="submit"
              data-testid="admin-login-btn"
              disabled={!passcode || loading}
              className="w-full py-3.5 bg-[#c5a059] text-[#0b0b0c] font-mono-ov text-xs tracking-[0.15em] uppercase rounded-full hover:bg-[#dfcca4] transition-colors disabled:opacity-50"
            >
              {loading ? "Checking…" : "Enter Inbox"}
            </button>
          </form>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="admin-leads-page" className="min-h-screen bg-[#0b0b0c] py-14 px-5 sm:px-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
          <div>
            <p className="overline-label mb-2">Sven Suits — Private</p>
            <h1 className="font-display text-3xl font-medium text-[#faf8f5]">Lead Inbox</h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-refresh-btn"
              onClick={loadLeads}
              className="flex items-center gap-2 px-5 py-2.5 border border-white/15 text-white/70 font-mono-ov text-[10px] tracking-[0.15em] uppercase rounded-full hover:border-[#c5a059] hover:text-[#dfcca4] transition-colors"
            >
              <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="flex items-center gap-2 px-5 py-2.5 border border-white/15 text-white/70 font-mono-ov text-[10px] tracking-[0.15em] uppercase rounded-full hover:border-red-400/60 hover:text-red-300 transition-colors"
            >
              <LogOut size={12} /> Sign Out
            </button>
          </div>
        </div>

        <p className="font-mono-ov text-[10px] tracking-[0.2em] uppercase text-white/40 mb-4">
          {leads.length} enquir{leads.length === 1 ? "y" : "ies"} · newest first
        </p>

        {leads.length === 0 ? (
          <div data-testid="admin-empty-state" className="border border-white/10 bg-[#141416] py-20 text-center">
            <Inbox className="mx-auto text-white/25 mb-4" size={32} />
            <p className="font-light text-white/50">No booking enquiries yet.</p>
          </div>
        ) : (
          <div className="border border-white/10 bg-[#141416] overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-white/10">
                  {["Received", "Name", "Phone", "Service", "Preferred", ""].map((h) => (
                    <th key={h} className="px-5 py-4 font-mono-ov text-[9px] tracking-[0.2em] uppercase text-[#c5a059] whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} data-testid={`admin-lead-row-${l.id}`} className="border-b border-white/5 last:border-0 hover:bg-white/[0.03] transition-colors">
                    <td className="px-5 py-4 text-xs font-light text-white/50 whitespace-nowrap">{fmtDate(l.created_at)}</td>
                    <td className="px-5 py-4 font-display text-sm text-white whitespace-nowrap">{l.name}</td>
                    <td className="px-5 py-4 text-sm font-light text-white/70 whitespace-nowrap">{l.phone}</td>
                    <td className="px-5 py-4 text-sm font-light text-white/70">{l.service}</td>
                    <td className="px-5 py-4 text-xs font-light text-white/50 whitespace-nowrap">
                      {[l.preferred_date, l.preferred_time].filter(Boolean).join(" · ") || "—"}
                    </td>
                    <td className="px-5 py-4">
                      <a
                        href={`https://wa.me/${String(l.phone).replace(/\D/g, "").replace(/^(?!91)/, "91")}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-[#25D366] hover:brightness-125 font-mono-ov text-[10px] tracking-wider uppercase"
                      >
                        <FaWhatsapp size={13} /> Reply
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </main>
  );
}
